// frontend/src/components/Auth/TurnstileWidget.tsx
// Cloudflare Turnstile on the email sign-in form (open-access plan §3.7). The
// token goes to Supabase as `captchaToken` with the request that emails a code;
// with CAPTCHA protection on in Supabase, a code request without one is
// refused. Google sign-in, code verification and session refresh never need it.
//
// The script is loaded from challenges.cloudflare.com (allowed in
// public/_headers' script-src and frame-src) on first use, not bundled: there
// is no npm package in the tree, and the API is a single global.

import { useEffect, useRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/** The site key, or '' when the widget is off (local dev, tests). Set
 *  REACT_APP_TURNSTILE_SITE_KEY per environment in the Pages dashboard. */
export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY ?? '';

interface TurnstileApi {
  render: (el: HTMLElement, options: Record<string, unknown>) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = () =>
        window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile did not load'));
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error('Turnstile did not load'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

export interface TurnstileWidgetProps {
  siteKey: string;
  /** Called with a fresh token, or null when it expires or the challenge fails. */
  onToken: (token: string | null) => void;
  /** Change this to get a new token: a token is single-use, so the form bumps
   *  it after every code request. */
  resetKey?: number;
}

const TurnstileWidget = ({ siteKey, onToken, resetKey = 0 }: TurnstileWidgetProps) => {
  const container = useRef<HTMLDivElement>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!siteKey || !container.current) return undefined;
    let widgetId: string | null = null;
    let cancelled = false;
    onTokenRef.current(null);

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !container.current) return;
        widgetId = turnstile.render(container.current, {
          sitekey: siteKey,
          theme: 'auto',
          size: 'flexible',
          callback: (token: string) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(null),
          'error-callback': () => onTokenRef.current(null),
        });
      })
      .catch(() => onTokenRef.current(null));

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [siteKey, resetKey]);

  if (!siteKey) return null;
  return <div ref={container} data-testid="turnstile-widget" className="flex justify-center" />;
};

export default TurnstileWidget;
